import { Box, Grid, Stack, Typography } from '@mui/material';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { getLockIcon, getLockStatus } from 'utils/Lock/Lock';
import BatteryStatusChip from 'v2/components/BatteryChip/BatteryStatusChip';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import EditableCard from 'v2/components/EditableCard/EditableCard';
import InfoGrid from 'v2/components/InfoGrid/InfoGrid';
import Loader from 'v2/components/Loader/Loader';
import useDeviceDetails from 'v2/hooks/useDeviceDetails/useDeviceDetails';
import { getLockTypeName, getOfficeModeDisplayText } from 'v2/utils/lock';
import EditDeviceDrawer from '../drawers/EditDeviceDrawer';

interface Props {
  deviceId: string;
}

const DeviceInfo: React.FC<Props> = ({ deviceId }) => {
  const { translation } = useTranslation();
  const { openDrawer } = useDrawerContext();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { device, deviceLoading } = useDeviceDetails({
    handleFetchError,
    translation,
    deviceId
  });

  const handleEditDeviceClick = () => {
    if (device) openDrawer(<EditDeviceDrawer device={device} />);
  };

  const isLoading = deviceLoading || device === undefined;
  const deviceType = device?.type;
  const deviceStatus = device ? getLockStatus(device) : undefined;

  const officeModeText = getOfficeModeDisplayText(device, translation);

  return (
    <EditableCard
      title={translation.device_info}
      editButtonId="editDevice"
      onEditClick={handleEditDeviceClick}
      spacing={2}
    >
      {!isLoading ? (
        <InfoGrid equalColumns>
          <InfoGrid.Label>{translation.serial_number}</InfoGrid.Label>
          <InfoGrid.Text>{device.hardwareIdentifier}</InfoGrid.Text>

          <InfoGrid.Label>{translation.device_type}</InfoGrid.Label>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2">{getLockTypeName(deviceType, translation)}</Typography>
            {getLockIcon(device, { color: 'action' })}
          </Stack>

          <InfoGrid.Label
            tooltip={
              <Grid container>
                <Typography variant="body2">{translation.office_mode_description}</Typography>
              </Grid>
            }
          >
            {translation.office_mode}
          </InfoGrid.Label>
          <InfoGrid.Text>{officeModeText}</InfoGrid.Text>

          <InfoGrid.Label>{translation.battery}</InfoGrid.Label>
          <Box sx={{ justifySelf: 'start' }}>
            {deviceStatus ? (
              <BatteryStatusChip level={deviceStatus.batteryStatus} />
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Box>
        </InfoGrid>
      ) : (
        <Loader />
      )}
    </EditableCard>
  );
};

export default DeviceInfo;
